// Rehber penceresindeki küçük ekran haritasının düzenleri. Her düzen, bölgelerin ızgaradaki yerini ve
// içlerine çizilen sade taslağı (gri çizgiler, kutular) tanımlar. Adımlar bölge anahtarıyla bir bölgeyi vurgular.
import React from 'react';
import { Box, Typography } from '@mui/material';

export type GuideLayout = 'card' | 'list' | 'report' | 'dialog' | 'tabbed' | 'split' | 'stacked';

export const LAYOUT_REGIONS = {
  card: ['header', 'strip', 'nav', 'content', 'summary', 'footer'],
  list: ['header', 'stats', 'filters', 'toolbar', 'table', 'pagination'],
  report: ['header', 'actions', 'filters', 'totals', 'table', 'pagination'],
  dialog: ['header', 'body', 'footer'],
  tabbed: ['header', 'tabs', 'body', 'footer'],
  split: ['header', 'list', 'form', 'footer'],
  stacked: ['header', 'form', 'list', 'footer'],
} as const;

export type GuideRegionKey = (typeof LAYOUT_REGIONS)[GuideLayout][number];

interface LayoutGrid {
  areas: string;
  columns: string;
  rows: string;
}

export const LAYOUT_GRID: Record<GuideLayout, LayoutGrid> = {
  card: {
    areas: '"header header header" "strip strip strip" "nav content summary" "footer footer footer"',
    columns: '24% 1fr 24%',
    rows: '30px 54px 1fr 30px',
  },
  list: {
    areas: '"header header" "stats stats" "filters toolbar" "table table" "pagination pagination"',
    columns: '1fr 34%',
    rows: '30px 46px 42px 1fr 26px',
  },
  report: {
    areas: '"header actions" "filters filters" "totals totals" "table table" "pagination pagination"',
    columns: '1fr 34%',
    rows: '42px 34px 40px 1fr 26px',
  },
  dialog: {
    areas: '"header" "body" "footer"',
    columns: '1fr',
    rows: '30px 1fr 30px',
  },
  tabbed: {
    areas: '"header" "tabs" "body" "footer"',
    columns: '1fr',
    rows: '30px 46px 1fr 30px',
  },
  split: {
    areas: '"header header" "list form" "footer footer"',
    columns: '38% 1fr',
    rows: '30px 1fr 30px',
  },
  // Üstte form, altta liste (ör. sevk adresleri)
  stacked: {
    areas: '"header" "form" "list" "footer"',
    columns: '1fr',
    rows: '30px 1fr 1fr 30px',
  },
};

const line = (w: string, key?: React.Key, h = 6) => (
  <Box key={key} sx={{ height: h, width: w, borderRadius: 3, bgcolor: 'action.selected' }} />
);

export const bars = (n: number, widths: string[] = ['80%', '60%', '70%']) => (
  <Box sx={{ display: 'grid', gap: 0.5, mt: 0.5 }}>
    {Array.from({ length: n }).map((_, i) => line(widths[i % widths.length], i))}
  </Box>
);

const chips = (labels: string[], selected = 0) => (
  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, overflow: 'hidden' }}>
    {labels.slice(0, 5).map((n, i) => (
      <Typography
        key={`${n}-${i}`}
        variant="caption"
        noWrap
        sx={{
          fontSize: 10, lineHeight: 1.5, px: 0.75, borderRadius: 0.5, flex: 'none',
          bgcolor: i === selected ? 'action.selected' : 'transparent',
          borderBottom: i === selected ? '2px solid' : 'none', borderColor: 'primary.main',
        }}
      >
        {n}
      </Typography>
    ))}
  </Box>
);

const buttonsRight = (count = 2) => (
  // Sağ üstte adım rozeti (20px) için yer bırakılır
  <Box sx={{ position: 'absolute', right: 30, top: 6, display: 'flex', gap: 0.5 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={i === count - 1
          ? { width: 30, height: 14, borderRadius: 0.5, bgcolor: 'primary.main', opacity: 0.8 }
          : { width: 30, height: 14, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}
      />
    ))}
  </Box>
);

// Bölge adının altında, satır içinde düğmeler (dar bölgelerde ad ile çakışmasın diye)
const buttonsInline = (count = 3) => (
  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={i === count - 1
          ? { width: 26, height: 10, borderRadius: 0.5, bgcolor: 'primary.main', opacity: 0.8, flex: 'none' }
          : { width: 26, height: 10, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', flex: 'none' }}
      />
    ))}
  </Box>
);

const tableRows = (rows: number) => (
  <Box sx={{ display: 'grid', gap: 0.5, mt: 0.5 }}>
    <Box sx={{ height: 8, borderRadius: 0.5, bgcolor: 'primary.main', opacity: 0.35 }} />
    {Array.from({ length: rows }).map((_, i) => (
      <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 0.5 }}>
        {line('100%', 'a', 5)}{line('90%', 'b', 5)}{line('70%', 'c', 5)}{line('60%', 'd', 5)}
      </Box>
    ))}
  </Box>
);

const statCards = (n = 4) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: 0.5, mt: 0.25 }}>
    {Array.from({ length: n }).map((_, i) => (
      <Box key={i} sx={{ height: 16, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
    ))}
  </Box>
);

const fieldGrid = (n: number) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mt: 0.75 }}>
    {Array.from({ length: n }).map((_, i) => (
      <Box key={i} sx={{ height: 14, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
    ))}
  </Box>
);

/** Bölge içi taslak. items: kart düzeninde sol bölüm adları, sekmeli düzende sekme adları. */
export const drawRegion = (layout: GuideLayout, region: string, items: string[]): React.ReactNode => {
  switch (`${layout}:${region}`) {
    case 'card:strip':
      return (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
          {[22, 30, 40].map(w => <Box key={w} sx={{ height: 12, width: `${w}%`, borderRadius: 0.5, bgcolor: 'action.selected' }} />)}
        </Box>
      );
    case 'card:nav':
      return (
        <Box sx={{ display: 'grid', gap: 0.4, mt: 0.5 }}>
          {items.slice(0, 6).map((n, i) => (
            <Typography
              key={`${n}-${i}`}
              variant="caption"
              noWrap
              sx={{ fontSize: 10, lineHeight: 1.4, px: 0.5, borderRadius: 0.5, bgcolor: i === 0 ? 'action.selected' : 'transparent' }}
            >
              {n}
            </Typography>
          ))}
        </Box>
      );
    case 'card:content':
      return bars(5);
    case 'card:summary':
      return bars(4, ['70%', '50%', '85%']);
    case 'card:footer':
    case 'dialog:footer':
    case 'tabbed:footer':
    case 'split:footer':
    case 'stacked:footer':
      return buttonsRight(2);
    case 'list:header':
      return buttonsRight(2);
    case 'list:stats':
      return statCards(4);
    case 'list:filters':
    case 'report:filters':
      return (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
          <Box sx={{ height: 10, flex: 2, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
          <Box sx={{ height: 10, flex: 1, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
        </Box>
      );
    case 'list:toolbar':
    case 'report:actions':
      return buttonsInline(3);
    case 'list:table':
    case 'report:table':
      return tableRows(5);
    case 'report:totals':
      return statCards(3);
    case 'dialog:body':
      return fieldGrid(6);
    case 'tabbed:tabs':
      return chips(items.length ? items : ['', '', '']);
    case 'tabbed:body':
      return fieldGrid(4);
    case 'stacked:list':
      return tableRows(3);
    case 'stacked:form':
      return fieldGrid(4);
    case 'split:list':
      return (
        <Box sx={{ display: 'grid', gap: 0.5, mt: 0.5 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ height: 10, borderRadius: 0.5, bgcolor: i === 1 ? 'action.selected' : 'transparent', border: '1px solid', borderColor: 'divider' }} />
          ))}
        </Box>
      );
    case 'split:form':
      return fieldGrid(4);
    default:
      return null;
  }
};
